/**
 * ContainerREADY window kit — one purchase box:
 * size → price → ATC → small Home Depot helper.
 * Defaults to first sellable size. Kit-unavailable sizes stay listed as
 * disabled “coming soon” reminders (e.g. 4×2 / 4×4 until variants exist).
 */
(function () {
  'use strict';

  function normalizeSizeKey(value) {
    return String(value || '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();
  }

  function toFtLabel(value) {
    var raw = String(value || '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) return '';
    var parts = raw.split(/\s*x\s*/i);
    if (parts.length < 2) return raw;
    var w = parts[0].replace(/'/g, '').replace(/ft/gi, '').trim();
    var h = parts[1].replace(/'/g, '').replace(/ft/gi, '').trim();
    if (!w || !h) return raw;
    return w + ' ft × ' + h + ' ft';
  }

  /** Visible nominal label only, e.g. 4×4 — never actual inches. */
  function toNominalLabel(value) {
    var raw = String(value || '')
      .replace(/×/g, 'x')
      .replace(/\s+/g, ' ')
      .trim();
    if (!raw) return '';
    var parts = raw.split(/\s*x\s*/i);
    if (parts.length < 2) return raw.replace(/'/g, '');
    var w = parts[0].replace(/'/g, '').replace(/ft/gi, '').trim();
    var h = parts[1].replace(/'/g, '').replace(/ft/gi, '').trim();
    if (!w || !h) return raw.replace(/'/g, '');
    return w + '×' + h;
  }

  function parseJson(el) {
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function formatMoneyCents(cents, format) {
    if (typeof formatMoney === 'function') {
      try {
        return formatMoney(cents, format);
      } catch (e) {}
    }
    var amount = (Number(cents) || 0) / 100;
    return '$' + amount.toFixed(2);
  }

  function findSizeEntryAny(data, size) {
    if (!data || !data.sizes) return null;
    var nk = normalizeSizeKey(size);
    var sizes = data.sizes;
    if (sizes[size]) return sizes[size];
    var keys = Object.keys(sizes);
    for (var i = 0; i < keys.length; i++) {
      if (normalizeSizeKey(keys[i]) === nk) return sizes[keys[i]];
    }
    return null;
  }

  function isHiddenSizeValue(sizeValue, data) {
    var nk = normalizeSizeKey(sizeValue);
    if (!nk) return true;
    var hidden = (data && data.hiddenSizes) || [];
    for (var i = 0; i < hidden.length; i++) {
      if (normalizeSizeKey(hidden[i]) === nk) return true;
    }
    var entry = findSizeEntryAny(data, sizeValue);
    if (entry && entry.public === false) return true;
    if (entry && entry.public === true) return false;
    return true;
  }

  function findPublicSizeEntry(data, size) {
    var entry = findSizeEntryAny(data, size);
    if (!entry || entry.public === false) return null;
    return entry;
  }

  function primaryRetailer(entry) {
    if (!entry || !entry.retailers || !entry.retailers.length) return null;
    for (var i = 0; i < entry.retailers.length; i++) {
      var r = entry.retailers[i];
      if (r && r.approved !== false && r.url) return r;
    }
    return null;
  }

  function escapeHtml(s) {
    return String(s || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  if (customElements.get('cc-window-package-details')) return;

  class CcWindowPackageDetails extends HTMLElement {
    connectedCallback() {
      this.sizeSelect = this.querySelector('[data-cc-size-select]');
      this.priceEl = this.querySelector('[data-cc-price]');
      this.hdHelper = this.querySelector('[data-cc-hd-helper]');
      this.hdMatch = this.querySelector('[data-cc-hd-match]');
      this.hdBrand = this.querySelector('[data-cc-hd-brand]');
      this.hdCta = this.querySelector('[data-cc-hd-cta]');
      this.kitPending = this.querySelector('[data-cc-kit-pending]');
      this.kitHeading = this.querySelector('[data-cc-kit-heading]');
      this.localSooner = this.querySelector('[data-cc-local-sooner]');
      this.localHint = this.querySelector('[data-cc-local-hint]');
      this.pickerAnchor = this.querySelector('[data-cc-window-picker-anchor]');
      this.buyAnchor = this.querySelector('[data-cc-window-buy-anchor]');
      this._kitHeadingDefault = this.kitHeading ? this.kitHeading.textContent : '';
      this.compatData = parseJson(this.querySelector('[data-cc-window-compat-data]')) || {
        sizes: {},
        hiddenSizes: [],
        publicSizeOrder: []
      };
      this.productVariants = parseJson(document.querySelector('[data-cc-window-product-variants]')) || [];
      this.sectionId = this.dataset.section;
      this.moneyFormat =
        this.dataset.currencyCodeEnabled === 'true'
          ? this.dataset.moneyWithCurrencyFormat || this.dataset.moneyFormat
          : this.dataset.moneyFormat;
      this._onVariantChange = this.onVariantChange.bind(this);
      this._applyingVariant = false;
      this._customerChoseSize = false;
      document.addEventListener('product:variant-change', this._onVariantChange);

      this.adoptControls();
      var self = this;
      window.requestAnimationFrame(function () {
        self.adoptControls();
      });
      window.setTimeout(function () {
        self.adoptControls();
      }, 0);

      this.bindSizeSelect();
      this.bindEmbeddedSizeSelect();
      this.bindLocalSooner();
      this.decorateCustomerSizeOptions();
      this.applyInitialSize();
    }

    decorateCustomerSizeOptions() {
      if (!this.sizeSelect) return;
      Array.prototype.forEach.call(this.sizeSelect.options, function (opt) {
        if (!opt.value) return;
        var kitReady = opt.getAttribute('data-kit-ready');
        var hasVariant = !!(opt.getAttribute('data-variant-id') || '').trim();
        var ready = kitReady === 'true' || (kitReady === null && hasVariant);
        if (!ready) {
          opt.disabled = true;
          opt.setAttribute('data-kit-ready', 'false');
          if (!/coming soon/i.test(opt.textContent || '')) {
            var base = toNominalLabel(opt.value) || opt.textContent;
            opt.textContent = base + ' — coming soon';
          }
        } else {
          opt.disabled = false;
          opt.setAttribute('data-kit-ready', 'true');
        }
      });
    }

    applyInitialSize() {
      var initialSize = parseJson(this.querySelector('[data-cc-window-initial-size]'));
      var initialPrice = parseJson(this.querySelector('[data-cc-window-initial-price]'));
      var initialVariantId = parseJson(this.querySelector('[data-cc-window-initial-variant-id]'));
      var size = initialSize;
      var price = initialPrice;
      var variantId = initialVariantId;

      if (!size || !variantId) {
        var fallback = this.resolveVariantForSize('') || null;
        if (!fallback && this.productVariants && this.productVariants.length) {
          for (var i = 0; i < this.productVariants.length; i++) {
            if (this.productVariants[i].available !== false) {
              fallback = this.productVariants[i];
              break;
            }
          }
        }
        if (fallback) {
          size = fallback.title;
          price = fallback.price;
          variantId = fallback.id;
        }
      }

      if (!size || !variantId) {
        this.clearSelectionState();
        return;
      }

      this.syncCustomerSelect(size);
      this.syncNativeSelect(size);
      this.applySizeSelection(size, price, variantId, false);
      if (this.hdHelper) this.hdHelper.hidden = false;
    }

    disconnectedCallback() {
      document.removeEventListener('product:variant-change', this._onVariantChange);
    }

    adoptControls() {
      this.adoptVariantPicker();
      this.adoptBuyForm();
    }

    bindLocalSooner() {
      if (!this.localSooner || this.localSooner.dataset.ccBound === 'true') return;
      this.localSooner.dataset.ccBound = 'true';
      var self = this;
      this.localSooner.addEventListener('click', function (e) {
        e.preventDefault();
        if (!self.localHint) return;
        var open = self.localHint.hidden;
        self.localHint.hidden = !open;
      });
    }

    resolveVariantForSize(size) {
      var nk = normalizeSizeKey(size);
      if (!nk) return null;
      var entry = findPublicSizeEntry(this.compatData, size);
      if (entry && entry.variantId) {
        var fromSot = String(entry.variantId);
        for (var i = 0; i < this.productVariants.length; i++) {
          if (String(this.productVariants[i].id) === fromSot) {
            return this.productVariants[i];
          }
        }
      }
      for (var j = 0; j < this.productVariants.length; j++) {
        if (normalizeSizeKey(this.productVariants[j].title) === nk) {
          return this.productVariants[j];
        }
      }
      return null;
    }

    clearSelectionState() {
      this._customerChoseSize = false;
      if (this.sizeSelect) this.sizeSelect.value = '';
      if (this.hdHelper) this.hdHelper.hidden = true;
      if (this.localHint) this.localHint.hidden = true;
      this.setKitAtcEnabled(false);
      if (this.kitPending) this.kitPending.hidden = true;
      if (this.priceEl) {
        this.priceEl.hidden = true;
        this.priceEl.textContent = '';
      }
      this.renderHdHelper(null, '');
    }

    bindSizeSelect() {
      if (!this.sizeSelect || this.sizeSelect.dataset.ccBound === 'true') return;
      this.sizeSelect.dataset.ccBound = 'true';
      var self = this;
      this.sizeSelect.addEventListener('change', function () {
        var opt = self.sizeSelect.options[self.sizeSelect.selectedIndex];
        var size = opt ? opt.value : '';
        if (!size) {
          self.clearSelectionState();
          return;
        }
        if (opt.disabled || opt.getAttribute('data-kit-ready') === 'false') {
          self.syncCustomerSelect(
            parseJson(self.querySelector('[data-cc-window-initial-size]')) || ''
          );
          return;
        }
        if (isHiddenSizeValue(size, self.compatData)) return;
        var variant = self.resolveVariantForSize(size);
        var variantId = variant ? variant.id : opt.getAttribute('data-variant-id') || '';
        var price = variant ? variant.price : opt.getAttribute('data-price') || '';
        if (!variantId) {
          self._customerChoseSize = true;
          self.setKitAtcEnabled(false);
          self.renderHdHelper(findPublicSizeEntry(self.compatData, size), size);
          if (self.hdHelper) self.hdHelper.hidden = false;
          return;
        }
        self.syncNativeSelect(size);
        self.applySizeSelection(size, price, variantId, true);
      });
    }

    syncNativeSelect(size) {
      var select =
        (this.pickerAnchor && this.pickerAnchor.querySelector('select')) ||
        this.querySelector('[data-cc-window-size]');
      if (!select) return;
      var nk = normalizeSizeKey(size);
      for (var i = 0; i < select.options.length; i++) {
        if (normalizeSizeKey(select.options[i].value) === nk) {
          select.selectedIndex = i;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return;
        }
      }
    }

    syncCustomerSelect(size) {
      if (!this.sizeSelect) return;
      var nk = normalizeSizeKey(size);
      for (var i = 0; i < this.sizeSelect.options.length; i++) {
        if (normalizeSizeKey(this.sizeSelect.options[i].value) === nk) {
          this.sizeSelect.selectedIndex = i;
          return;
        }
      }
    }

    bindEmbeddedSizeSelect() {
      var select =
        (this.pickerAnchor && this.pickerAnchor.querySelector('[data-cc-window-size]')) ||
        this.querySelector('[data-cc-window-size]');
      if (!select || select.dataset.ccBound === 'true') return;
      select.dataset.ccBound = 'true';
      this.applyPublicOptionVisibility(select);
      var self = this;
      select.addEventListener('change', function () {
        var opt = select.options[select.selectedIndex];
        if (!opt || opt.disabled || opt.hidden || !opt.value) {
          self.clearSelectionState();
          return;
        }
        self.syncCustomerSelect(opt.value);
        self.applySizeSelection(opt.value, opt.getAttribute('data-price'), opt.getAttribute('data-variant-id'), true);
      });
    }

    adoptVariantPicker() {
      if (!this.pickerAnchor || !this.sectionId) return;
      if (this.pickerAnchor.querySelector('variant-selects')) {
        this.decorateDropdown();
        return;
      }
      var picker = document.getElementById('variant-selects-' + this.sectionId);
      if (picker && picker.parentNode !== this.pickerAnchor) {
        this.pickerAnchor.appendChild(picker);
      }
      this.decorateDropdown();
    }

    decorateDropdown() {
      var picker = this.pickerAnchor && this.pickerAnchor.querySelector('variant-selects');
      if (!picker) return;
      picker.classList.add('cc-window-native-picker');
      picker.setAttribute('aria-hidden', 'true');
      if (this.pickerAnchor) this.pickerAnchor.setAttribute('aria-hidden', 'true');
      var select = picker.querySelector('select');
      if (!select) return;
      select.setAttribute('tabindex', '-1');
      this.applyPublicOptionVisibility(select);
      if (select.dataset.ccCcSizeBound !== 'true') {
        select.dataset.ccCcSizeBound = 'true';
        var self = this;
        select.addEventListener('change', function () {
          window.setTimeout(function () {
            if (!self._customerChoseSize) return;
            var opt = select.options[select.selectedIndex];
            if (!opt || !opt.value) return;
            var size = opt.value;
            var entry = findPublicSizeEntry(self.compatData, size);
            var formInput = self.getProductFormIdInput();
            var variantId = formInput && formInput.value ? formInput.value : entry && entry.variantId;
            var priceCents = null;
            if (formInput && formInput.dataset && formInput.dataset.price) {
              priceCents = formInput.dataset.price;
            }
            self.syncCustomerSelect(size);
            self.syncProductFormVariantId(variantId);
            self.updateFromSize(size, priceCents, variantId);
          }, 0);
        });
      }
    }

    applyPublicOptionVisibility(select) {
      if (!select) return;
      var self = this;
      Array.prototype.forEach.call(select.options, function (opt) {
        if (!opt.value) {
          opt.disabled = false;
          opt.hidden = false;
          return;
        }
        var hide = isHiddenSizeValue(opt.value, self.compatData);
        if (hide) {
          opt.disabled = true;
          opt.hidden = true;
          return;
        }
        var entry = findPublicSizeEntry(self.compatData, opt.value);
        var kitReady = !!(entry && entry.variantId) || !!self.resolveVariantForSize(opt.value);
        opt.hidden = false;
        opt.disabled = !kitReady;
        var friendly = toNominalLabel(opt.value) || toFtLabel(opt.value);
        if (friendly) {
          opt.textContent = kitReady ? friendly : friendly + ' — coming soon';
        }
      });
    }

    adoptBuyForm() {
      if (!this.buyAnchor || !this.sectionId) return;
      if (this.buyAnchor.querySelector('product-form')) return;
      var form =
        document.querySelector('#ProductInfo-' + this.sectionId + ' product-form') ||
        document.querySelector('product-form[data-section="' + this.sectionId + '"]');
      if (!form || form.parentNode === this.buyAnchor) return;
      var previousParent = form.parentElement;
      this.buyAnchor.appendChild(form);
      if (
        previousParent &&
        !previousParent.querySelector('product-form') &&
        !previousParent.querySelector('pickup-availability') &&
        !previousParent.querySelector('cc-window-package-details')
      ) {
        previousParent.setAttribute('data-cc-window-buy-shell', 'empty');
      }
    }

    getProductFormIdInput() {
      var selectors = [];
      if (this.sectionId) {
        selectors.push('#product-form-' + this.sectionId + ' input[name="id"]');
        selectors.push('#ProductInfo-' + this.sectionId + ' input[name="id"]');
      }
      selectors.push('cc-window-package-details input[name="id"]');
      selectors.push('product-form input[name="id"]');
      selectors.push('form[action*="/cart/add"] input[name="id"]');
      for (var i = 0; i < selectors.length; i++) {
        var el = document.querySelector(selectors[i]);
        if (el) return el;
      }
      return this.querySelector('input[name="id"]');
    }

    syncProductFormVariantId(variantId) {
      if (variantId === null || variantId === undefined || variantId === '') return;
      var idStr = String(variantId);
      var inputs = document.querySelectorAll(
        '#product-form-' +
          (this.sectionId || '') +
          ' input[name="id"], #product-form-installment input[name="id"], product-form input[name="id"], form[action*="/cart/add"] input[name="id"]'
      );
      if (!inputs.length) {
        var single = this.getProductFormIdInput();
        if (single) {
          single.value = idStr;
          single.disabled = false;
          single.dispatchEvent(new Event('change', { bubbles: true }));
        }
        return;
      }
      Array.prototype.forEach.call(inputs, function (input) {
        input.value = idStr;
        input.disabled = false;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    setKitAtcEnabled(enabled) {
      var addButton =
        this.querySelector('[name="add"]') ||
        document.querySelector('#product-form-' + this.sectionId + ' [name="add"]');
      if (addButton) {
        if (!enabled) addButton.setAttribute('disabled', 'disabled');
        else addButton.removeAttribute('disabled');
      }
      // Kit controls stay in their own section. Gated sizes hide ATC/price
      // and show coming-soon — they never hand the kit area over to HD.
      if (this.buyAnchor) {
        this.buyAnchor.hidden = !enabled;
      }
      if (this.priceEl) {
        this.priceEl.hidden = !enabled;
      }
      if (this.kitPending) {
        this.kitPending.hidden = enabled || !this._customerChoseSize;
      }
    }

    applySizeSelection(size, priceCents, variantId, emitEvent) {
      if (!size) {
        this.clearSelectionState();
        return;
      }
      this._customerChoseSize = true;

      var entry = findPublicSizeEntry(this.compatData, size);
      var variant = this.resolveVariantForSize(size);
      var resolvedId = (variant && variant.id) || variantId || (entry && entry.variantId) || null;
      if (resolvedId === '') resolvedId = null;
      var resolvedPrice = priceCents;
      if ((resolvedPrice === null || resolvedPrice === undefined || resolvedPrice === '') && variant) {
        resolvedPrice = variant.price;
      }

      if (resolvedId) this.syncProductFormVariantId(resolvedId);
      this.updateFromSize(size, resolvedPrice, resolvedId);
      this.setKitAtcEnabled(!!resolvedId);

      if (emitEvent && resolvedId) {
        this._applyingVariant = true;
        document.dispatchEvent(
          new CustomEvent('product:variant-change', {
            bubbles: true,
            detail: {
              variant: {
                id: resolvedId,
                option1: size,
                title: size,
                price: Number(resolvedPrice)
              },
              sectionId: this.sectionId
            }
          })
        );
        this._applyingVariant = false;
      }
    }

    onVariantChange(event) {
      if (this._applyingVariant) return;
      if (!this._customerChoseSize) return;
      var detail = (event && event.detail) || {};
      if (this.sectionId && detail.sectionId && detail.sectionId !== this.sectionId) return;
      var variant = detail.variant;
      if (!variant) {
        this.clearSelectionState();
        return;
      }
      var size = variant.option1 || variant.title || '';
      this.syncProductFormVariantId(variant.id);
      this.syncCustomerSelect(size);
      this.updateFromSize(size, variant.price, variant.id);
      this.setKitAtcEnabled(!!variant.id);
      this.decorateDropdown();
    }

    updateFromSize(size, priceCents, variantId) {
      if (!size) {
        this.clearSelectionState();
        return;
      }
      this._customerChoseSize = true;
      if (this.hdHelper) this.hdHelper.hidden = false;

      if (this.priceEl && priceCents !== null && priceCents !== undefined && priceCents !== '') {
        this.priceEl.textContent = formatMoneyCents(priceCents, this.moneyFormat);
      }

      var entry = findPublicSizeEntry(this.compatData, size);
      this.renderHdHelper(entry, size);

      var hasVariant = !!(variantId || (entry && entry.variantId) || this.resolveVariantForSize(size));
      this.setKitAtcEnabled(hasVariant);

      if (variantId) this.syncProductFormVariantId(variantId);
    }

    renderHdHelper(entry, size) {
      var retailer = primaryRetailer(entry);
      var nominal = toNominalLabel(size);

      if (!entry || !retailer) {
        if (this.hdCta) {
          this.hdCta.hidden = true;
          this.hdCta.removeAttribute('href');
          this.hdCta.removeAttribute('data-item');
          this.hdCta.removeAttribute('data-model');
          this.hdCta.removeAttribute('data-actual');
        }
        if (this.hdMatch) this.hdMatch.textContent = 'Matching window';
        if (this.hdBrand) this.hdBrand.textContent = 'JELD-WEN V-2500';
        return;
      }

      if (this.hdMatch) {
        this.hdMatch.textContent = nominal ? 'Matching ' + nominal + ' window' : 'Matching window';
      }
      if (this.hdBrand) this.hdBrand.textContent = retailer.brand || 'JELD-WEN V-2500';

      if (this.hdCta) {
        this.hdCta.hidden = false;
        this.hdCta.href = retailer.url;
        this.hdCta.textContent = nominal
          ? 'ORDER MATCHING ' + nominal + ' WINDOW →'
          : 'ORDER MATCHING WINDOW →';
        // Internet # / model / actual inches stay in data only — never in visible UI.
        if (retailer.item) this.hdCta.setAttribute('data-item', retailer.item);
        else this.hdCta.removeAttribute('data-item');
        if (retailer.model) this.hdCta.setAttribute('data-model', retailer.model);
        else this.hdCta.removeAttribute('data-model');
        if (entry.actual) this.hdCta.setAttribute('data-actual', entry.actual);
        else this.hdCta.removeAttribute('data-actual');
      }
    }
  }

  customElements.define('cc-window-package-details', CcWindowPackageDetails);
})();
